/**
 * Задачи всех досок подразделения — одним списком.
 *
 * Вход — иконка сразу за селектом досок. Канбан показывает одну доску, а
 * вопрос «что у меня горит» не ограничен доской: задачи одного человека
 * разбросаны по доскам подразделения. Отсюда быстрый фильтр «Мои сегодня» —
 * мои задачи со сроком на сегодня и все мои просроченные.
 *
 * Фильтры — те же, что на доске, и так же живут в адресе (§16.4). Нет
 * только тегов: теги принадлежат доске, и у соседних досок они разные.
 * Период здесь — не срок, а период работы, по правилу «Отчёта по работам»:
 * список за период обязан совпадать с отчётом за тот же период.
 *
 * Список листается страницами по PAGE_SIZE. Задачи приходят целиком: на
 * клиенте их сортируют по любому столбцу, а выгрузка берёт весь список,
 * а не одну страницу.
 * Карточка открывается поверх списка по ?task=КЛЮЧ, как в архиве: список
 * под ней остаётся на месте, ссылку можно переслать.
 */

import { DepartmentCode } from "@taskflow/contracts";
import { CalendarClock, ClipboardList, ListChecks } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "@/app/hooks";
import { useDepartmentsQuery, useUserDirectoryQuery } from "@taskflow/client-core/directory";
import { useTopics, type WsError } from "@taskflow/client-core";
import { formatDate } from "@/shared/lib/date";
import { useIsPhone } from "@/shared/lib/media";
import {
  AvatarGroup,
  Button,
  type Column,
  DataTable,
  EmptyState,
  Pagination,
  PriorityBadge,
  SegmentedControl,
  type SegmentOption,
  type SortState,
  Tag,
} from "@/shared/ui";
import { Download } from "@/shared/ui/motionIcons";

import { usePrioritiesQuery, useTaskTypesQuery } from "@taskflow/client-core/admin";
import { useBoardsQuery } from "@taskflow/client-core/boards";
import { BoardSwitcher } from "../boards/BoardSwitcher";
import { useDepartmentTabs } from "@taskflow/client-core/boards";
import {
  exportFiltersLabel,
  filtersToQuery,
  type TaskFilters,
  type TaskListItem,
  type TaskStateFilter,
  useTasksQuery,
} from "@taskflow/client-core/tasks";
import { isSortField, sortDepartmentTasks } from "./departmentTasksSort";
import { ExportTasksDialog } from "./ExportTasksDialog";
import { PhoneTaskbookBar } from "./PhoneTaskbookBar";
import { TaskFilterBar } from "./TaskFilterBar";
import { TaskPanel } from "./TaskPanel";
import { useTaskFilters } from "./useTaskFilters";
import { WorkReportDialog } from "./WorkReportDialog";

export function DepartmentTasksPage() {
  const [params, setParams] = useSearchParams();
  const user = useAppSelector((state) => state.auth.user);
  const departments = useDepartmentsQuery();
  const boards = useBoardsQuery();
  const tabs = useDepartmentTabs();

  const requested = tabs.order.find((code) => code === params.get("department"));
  const primaryCode = departments.data?.data.find(
    (item) => item.id === user?.primaryDepartmentId,
  )?.code;
  const code = requested ?? primaryCode ?? DepartmentCode.OA;
  const departmentId = departments.data?.data.find((item) => item.code === code)?.id;

  // Задачи, созданные и изменённые на досках подразделения, — без перезагрузки.
  const siblings = (boards.data?.data ?? []).filter((board) => board.departmentCode === code);
  useTopics(siblings.map((board) => `board:${board.id}`));

  const { filters, validation, activeCount, setFilter, setFilters, reset } = useTaskFilters();
  const phone = useIsPhone();
  const [searchInput, setSearchInput] = useState(filters.search);

  const directory = useUserDirectoryQuery({});
  const priorities = usePrioritiesQuery({});
  const taskTypes = useTaskTypesQuery({});

  // Параметры списка — одни на экран и выгрузку: файл обязан содержать
  // ровно то, что показано.
  const query: TaskFilters = {
    ...(departmentId === undefined ? {} : { departmentId }),
    ...filtersToQuery(filters, "work"),
  };
  const tasks = useTasksQuery(query, {
    // Без подразделения запрос вернул бы задачи всех досок сразу.
    skip: departmentId === undefined || validation.invalidRange,
  });
  const [exporting, setExporting] = useState(false);
  const [reporting, setReporting] = useState(false);

  const assigneeOptions = (directory.data?.data ?? []).map((person) => ({
    id: person.id,
    label: `${person.lastName} ${person.firstName}`,
  }));
  const priorityOptions = (priorities.data?.data ?? []).map((item) => ({
    id: item.id,
    label: item.name,
  }));
  const typeOptions = (taskTypes.data?.data ?? []).map((item) => ({
    id: item.id,
    label: item.name,
  }));

  // Сортировка — по любому столбцу, и, как фильтры, живёт в адресе:
  // пересланная ссылка открывает список в том же порядке. Без выбора —
  // ближайший срок сверху: список читают, чтобы понять, что делать первым.
  const sortParam = params.get("sort");
  const sortField = isSortField(sortParam) ? sortParam : null;
  const sortDirection = params.get("dir") === "desc" ? "desc" : "asc";
  const changeSort = (next: SortState) =>
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.set("sort", next.field);
        updated.set("dir", next.direction);
        return updated;
      },
      // Как и фильтры: сортировка не засоряет историю кнопки «назад».
      { replace: true },
    );

  const priorityRank = useMemo(
    () =>
      new Map(
        (priorities.data?.data ?? []).map((item, index) => [item.name, item.position ?? index]),
      ),
    [priorities.data],
  );
  const rows = useMemo(
    () =>
      sortDepartmentTasks(
        tasks.data?.data ?? [],
        sortField === null ? null : { field: sortField, direction: sortDirection },
        priorityRank,
      ),
    [tasks.data, sortField, sortDirection, priorityRank],
  );

  // Другая выборка или порядок — снова с первой страницы: на пятой
  // странице нового списка человек оказался бы посреди незнакомых строк.
  const [page, setPage] = useState(0);
  const listKey = JSON.stringify([query, sortField, sortDirection]);
  const [pagedKey, setPagedKey] = useState(listKey);
  if (pagedKey !== listKey) {
    setPagedKey(listKey);
    setPage(0);
  }
  // Задачу удалили или она ушла из выборки — страница не остаётся пустой.
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = rows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const scroller = useRef<HTMLDivElement>(null);
  const turnPage = (next: number) => {
    setPage(next);
    // Новая страница читается сверху, а не с того места, где кончилась прежняя.
    if (scroller.current !== null) scroller.current.scrollTop = 0;
  };

  const openKey = params.get("task");
  const openTask = (key: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("task", key);
      return next;
    });
  const closeTask = () =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("task");
      return next;
    });

  const label = tabs.labels[code] ?? code;

  const pageHeader = (
    <header className="flex shrink-0 flex-col border-b border-line bg-surface px-4 py-2.5">
      <h1 className="sr-only">{`Задачи подразделения ${label}`}</h1>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BoardSwitcher departmentCode={code} boardId={null} view="list" />
        <div className="flex flex-wrap items-center gap-2">
          {/* Отчёт по работам — по форме заказчика, только для отдела ОА. */}
          {code === DepartmentCode.OA && (
            <Button
              size="sm"
              variant="secondary"
              icon={ClipboardList}
              title="Выполненные и текущие задачи досок ОА за период по тегу — в XLSX или PDF"
              onClick={() => setReporting(true)}
            >
              Отчёт по работам
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            icon={Download}
            disabled={rows.length === 0}
            title="Выгрузить показанный список с группировкой по проектам"
            onClick={() => setExporting(true)}
          >
            Выгрузить
          </Button>
        </div>
      </div>
    </header>
  );

  const filterBar = (
    <TaskFilterBar
      filters={filters}
      validation={validation}
      activeCount={activeCount}
      setFilter={setFilter}
      setFilters={setFilters}
      reset={reset}
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      leading={
        <>
          <Button
            size="sm"
            variant={filters.myToday ? "primary" : "secondary"}
            icon={CalendarClock}
            aria-pressed={filters.myToday}
            title="Мои задачи со сроком на сегодня и мои просроченные"
            onClick={() => setFilter("myToday", !filters.myToday)}
          >
            Мои сегодня
          </Button>
          <SegmentedControl<TaskStateFilter>
            size="sm"
            label="Фильтр по статусу"
            value={filters.state}
            onChange={(value) => setFilter("state", value)}
            options={STATE_OPTIONS}
          />
        </>
      }
      assignees={assigneeOptions}
      priorities={priorityOptions}
      types={typeOptions}
      periodMode="work"
    />
  );

  return (
    <div className="flex h-full flex-col">
      {phone ? (
        <PhoneTaskbookBar
          title={`${label} · Список задач`}
          activeFilters={activeCount}
          header={pageHeader}
          filters={filterBar}
        />
      ) : (
        <>
          {pageHeader}
          {filterBar}
        </>
      )}

      {exporting && (
        <ExportTasksDialog
          title={`Задачи подразделения ${label}`}
          filters={query}
          filtersLabel={exportFiltersLabel(filters, {
            assignees: assigneeOptions,
            priorities: priorityOptions,
            types: typeOptions,
          })}
          keys={rows.map((task) => task.key)}
          onClose={() => setExporting(false)}
        />
      )}

      {reporting && <WorkReportDialog onClose={() => setReporting(false)} />}

      {validation.invalidRange && (
        <p role="alert" className="bg-danger-soft px-4 py-2 text-[13px] font-semibold text-danger">
          Дата «с» позже даты «по» — задайте корректный период, чтобы увидеть задачи
        </p>
      )}

      <div ref={scroller} className="min-h-0 flex-1 overflow-auto p-4">
        {boards.data !== undefined && siblings.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={`В подразделении ${label} нет доступных досок`}
            description="Задачи появятся здесь, когда вас добавят в доску подразделения."
          />
        ) : (
          <DataTable
            caption={
              filters.myToday
                ? "Мои задачи на сегодня и просроченные"
                : `Задачи подразделения ${label}`
            }
            columns={COLUMNS}
            rows={pageRows}
            rowKey={(task) => task.key}
            onRowClick={(task) => openTask(task.key)}
            sort={sortField === null ? null : { field: sortField, direction: sortDirection }}
            onSortChange={changeSort}
            loading={tasks.isLoading || departments.isLoading}
            error={
              tasks.error === undefined ? null : { message: (tasks.error as WsError).data.message }
            }
            onRetry={() => void tasks.refetch()}
            empty={
              filters.myToday
                ? {
                    title: "На сегодня задач нет",
                    description: "Ни одна ваша задача не просрочена и не ждёт сегодняшнего срока.",
                  }
                : activeCount > 0
                  ? {
                      title: "Ничего не найдено",
                      description: "Измените или сбросьте фильтры.",
                    }
                  : { title: "В подразделении пока нет задач" }
            }
          />
        )}
        <Pagination
          hasPrevious={currentPage > 0}
          hasMore={currentPage < lastPage}
          onPrevious={() => turnPage(currentPage - 1)}
          onNext={() => turnPage(currentPage + 1)}
          total={rows.length}
          shown={pageRows.length}
          start={currentPage * PAGE_SIZE + 1}
        />
      </div>

      {openKey !== null && <TaskPanel taskKey={openKey} onClose={closeTask} />}
    </div>
  );
}

/** Строк на странице: столько читается без долгой прокрутки. */
const PAGE_SIZE = 50;

/** «Готово» — работа окончена, как её понимают просрочка и отчётность. */
const STATE_OPTIONS: SegmentOption<TaskStateFilter>[] = [
  { value: "", label: "Все" },
  { value: "open", label: "Открыто" },
  { value: "done", label: "Готово" },
];


const COLUMNS: Column<TaskListItem>[] = [
  {
    key: "key",
    sortable: true,
    header: "Задача",
    render: (task) => (
      <div className="min-w-40">
        <span className="font-mono text-[11px] text-faint">{task.key}</span>
        <p className="font-semibold text-fg wrap-anywhere">{task.title}</p>
      </div>
    ),
  },
  {
    key: "board",
    sortable: true,
    header: "Доска",
    width: "170px",
    render: (task) => (
      <span className="text-[13px] text-muted wrap-anywhere">{task.boardName ?? "—"}</span>
    ),
  },
  {
    key: "project",
    sortable: true,
    header: "Проект",
    width: "150px",
    render: (task) => (
      <span className="text-[13px] text-muted wrap-anywhere">{task.projectName ?? "—"}</span>
    ),
  },
  {
    key: "status",
    sortable: true,
    header: "Статус",
    width: "140px",
    render: (task) =>
      task.statusLabel === null || task.statusLabel === undefined ? (
        <span className="text-faint">—</span>
      ) : (
        <Tag tone="neutral" size="sm">
          {task.statusLabel}
        </Tag>
      ),
  },
  {
    key: "priority",
    sortable: true,
    header: "Приоритет",
    width: "130px",
    render: (task) =>
      task.priorityName === null || task.priorityName === undefined ? (
        <span className="text-faint">—</span>
      ) : (
        <PriorityBadge name={task.priorityName} color={task.priorityColor} />
      ),
  },
  {
    key: "assignees",
    sortable: true,
    header: "Исполнители",
    width: "120px",
    render: (task) =>
      task.assignees.length === 0 ? (
        <span className="text-faint">—</span>
      ) : (
        <AvatarGroup people={task.assignees} />
      ),
  },
  {
    key: "due",
    sortable: true,
    header: "Срок",
    width: "110px",
    render: (task) =>
      task.dueDate === null || task.dueDate === undefined ? (
        <span className="text-faint">—</span>
      ) : (
        <span className={task.overdue === true ? "font-semibold text-danger" : "text-fg"}>
          {formatDate(task.dueDate)}
        </span>
      ),
  },
];
